// file path: src\Utils\taskActions.ts

import { enhanceElementAppearance } from "../services/common.functions";
import { insertProjectTask, updateProjectTask } from "../services/project-task.service";
import Task, { GroupByColumn, GroupByPositions, SortColumn, SortCondition, SubTask, TaskGroup } from "../Types/project-task.type";
import { sortItemsAction } from "./SortingActions";


export function sortTaskAction(tasks: Task[], conditions: SortCondition[]) {
  if (conditions.length) {
    sortItemsAction(tasks, conditions);
  } else {
    sortItemsAction(tasks, [{ column: SortColumn.Position, order: "asc" }]);
  }
}

export function groupTasksAction(tasks: Task[], keys: GroupByColumn[]): TaskGroup[] {
  if (keys.length < 1) return [];

  // Function to generate a unique key for grouping based on the provided keys
  function generateKey(task: Task): string {
    return keys.map((key) => `${key}:${task[key as keyof Task]}`).join('|');
  }

  const groups: Record<string, TaskGroup> = {};

  tasks.forEach((task) => {
    const groupKey = generateKey(task);

    // Initialize a new group if it doesn't exist
    if (!groups[groupKey]) {
      groups[groupKey] = { attributes: {}, tasks: [] };
      keys.forEach((key) => {
        groups[groupKey].attributes[key] = task[key as keyof Task] as string;
      });
    }

    // Add the task to the appropriate group
    groups[groupKey].tasks.push(task);
  });

  const newGroups: TaskGroup[] = Object.values(groups);

  if (newGroups.length > 0) {
    // Assign group and task indices for easier referencing
    // Create new task objects to avoid mutating frozen objects
    newGroups.forEach((group, groupIndex) => {
      group.tasks = group.tasks.map((task, taskIndex) => {
        const updatedSubTasks = task.sub_tasks.map((subTask, subTaskIndex) => ({
          ...subTask,
          groupIndex: groupIndex,
          groupTaskIndex: taskIndex,
          groupSubTaskIndex: subTaskIndex
        }));

        return {
          ...task,
          groupIndex: groupIndex,
          groupTaskIndex: taskIndex,
          sub_tasks: updatedSubTasks
        };
      });
    });
  }

  return newGroups;
}

export function repositionTaskGroupsAction(taskGroups: TaskGroup[], groupPositions: GroupByPositions) {
  taskGroups.sort((a, b) => {
    const aKey = a.attributes.section || "";
    const bKey = b.attributes.section || "";
    const aPosition = groupPositions[aKey] ?? 0;
    const bPosition = groupPositions[bKey] ?? 0;
    return aPosition - bPosition;
  })
}

export function beforeSetTasks(tasks: Task[]): Task[] {
  return tasks.map((task, taskIndex) => {
    const updatedSubTasks = task.sub_tasks.map((subTask, subTaskIndex) => ({
      ...subTask,
      taskIndex: taskIndex,
      subTaskIndex: subTaskIndex
    }));

    return {
      ...task,
      taskIndex: taskIndex,
      sub_tasks: updatedSubTasks
    };
  });
}

export function getGroupIndex(taskGroups: any[], taskId: number): number {
  return taskGroups.findIndex((item) =>
    item.tasks.map((item: Task) => item.task_id).includes(taskId)
  );
}
export function getIndex(tasks: Task[], task_id: number) {
  return tasks.findIndex((item) => item.task_id === task_id);
}
export function getSubTaskIndex(items: SubTask[], sub_task_id: number) {
  return items.findIndex((item) => item.sub_task_id === sub_task_id);
}

export function getGroupIndexFromSubtask(
  taskGroups: any[],
  taskIndex: number,
  subtaskIndex: number,
  subtaskId: number
): number {
  return taskGroups.findIndex((item) => {
    const task = item.tasks[taskIndex];
    if (!task) return false;
    const subTask = task.sub_tasks[subtaskIndex];
    return subTask && subTask.subtaskId === subtaskId;
  });
}

export function updateTaskAction(
  tasks: Task[],
  taskIndex: number,
  updatedTask: Partial<Task>,
  conditions: SortCondition[],
  role: string
) {
  const task = tasks[taskIndex];
  tasks[taskIndex] = { ...task, ...updatedTask };
  const newTask = tasks[taskIndex];
  const apiCall = newTask.notInserted ? insertProjectTask : updateProjectTask
  return apiCall(
    newTask.project_id,
    newTask.task_id,
    newTask.sub_task_id, {
    section: task.section,
    assignee_id: task.assignee_id,
    ...updatedTask
  }, role).then(() => {
    if (newTask.notInserted) {
      delete newTask.notInserted;
    }
    sortTaskAction(tasks, conditions);
  })
}

export const updateSubtask = (
  tasks: Task[],
  taskIndex: number,
  subtaskIndex: number,
  updatedSubtask: Partial<SubTask>,
  role: string
) => {
  const subtask = tasks[taskIndex].sub_tasks[subtaskIndex];
  const dataToPost = { ...subtask, ...updatedSubtask }
  tasks[taskIndex].sub_tasks[subtaskIndex] = dataToPost;
  return updateProjectTask(
    subtask.project_id,
    subtask.task_id,
    subtask.sub_task_id,
    updatedSubtask,
    role
  );
};

export const glowEffect = (
  selector: string,
  focusInput = false,
  startAfter = 10,
  stopAfter = 3000
) => {
  setTimeout(() => {
    const element = document.querySelector(selector);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      element.classList.add("glow-effect");
      focusInput && element.querySelector(`input`)?.focus();

      setTimeout(() => {
        element.classList.remove("glow-effect");
      }, stopAfter);
    }
  }, startAfter);
};

export const focusRowInput = (selector: string, startAfter = 10) => {
  setTimeout(() => {
    const element = document.querySelector(selector);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      element.querySelector(`input`)?.focus();
    }
  }, startAfter);
};

export const foccusTaskRow = (taskId: number, startAfter = 10) => {
  enhanceElementAppearance(`#task-${taskId}`, {
    focusInput: false,
    glow: true,
    startAfter,
    stopAfter: 2000,
  });
};

export const foccusTaskInput = (taskId: number, startAfter = 10) => {
  enhanceElementAppearance(`#task-${taskId}`, { focusInput: true, startAfter });
};

export const foccusSubTaskInput = (taskId: number, subTaskId: number) => {
  enhanceElementAppearance(`#task-${taskId}-sub-task-${subTaskId}`, {
    focusInput: true,
  });
};

// export const addTaskAction = (
//   projectId: number,
//   tasks: Task[],
//   taskGroups: TaskGroup[],
//   groupIndex: number,
//   groupByColumns: GroupByColumn[],
//   taskIndex: number
// ): Task => {
//   const newTask = createBlankTask(projectId, tasks, 0) as any;
//   const group = groupIndex >= 0 ? taskGroups[groupIndex] : null;
//   const tableTasks = group ? group.tasks : tasks;

//   if (tableTasks.length > 0) {
//     groupByColumns.forEach((item) => {
//       newTask[item] = tableTasks[tableTasks.length - 1][item];
//     });
//   }

//   if (taskIndex >= 0) {
//     tasks.splice(taskIndex + 1, 0, newTask);
//   } else if (group) {
//     const groupLastTask = group.tasks[group.tasks.length - 1];
//     tasks.splice((groupLastTask.taskIndex ?? 0) + 1, 0, newTask);
//   } else {
//     tasks.push(newTask);
//   }
//   return newTask as Task;
// };


export const addTaskAction = (
  projectId: number,
  tasks: Task[],
  taskGroups: TaskGroup[],
  groupIndex: number,
  groupByColumns: GroupByColumn[],
  taskIndex: number,
  taskData: Partial<Task> = {} // Allow additional task properties
): Task => {
  const newTask: Task = {
    ...createBlankTask(projectId, tasks), // Generate a blank task
    ...taskData, // Merge additional task data
  };

  const group = groupIndex >= 0 ? taskGroups[groupIndex] : null;
  const tableTasks = group ? group.tasks : tasks;

  if (tableTasks.length > 0) {
    groupByColumns.forEach((item) => {
      newTask[item] = tableTasks[tableTasks.length - 1][item] as never;
    });
  }
  const groupLastTask = group ? group.tasks[group.tasks.length - 1] : false;

  if (taskIndex >= 0) {
    tasks.splice(taskIndex + 1, 0, newTask);
  } else if (group && groupLastTask) {
    tasks.splice((groupLastTask.taskIndex ?? 0) + 1, 0, newTask);
  } else {
    tasks.push(newTask);
  }

  return newTask;
};


export const createBlankTask = (projectId: number, tasks: Task[]): Task => {
  const task_id = tasks.length > 0 ? Math.max(...tasks.map((i) => i.task_id)) + 1 : 1;
  return {
    task_id,
    project_id: projectId,
    position: tasks.length,
    sub_task_id: 0,
    comments_count: 0,
    task: "",
    sub_tasks: [],
    status: "Not Started",
    hours_tracked: 0,
    notInserted: true
  }
}
export const addSectionAction = (
  projectId: number,
  tasks: Task[],
  section: string
): Task => {
  const newTask: Task = {
    ...createBlankTask(projectId, tasks),
    section
  };
  tasks.push(newTask);
  return newTask;
};

export const addSubTaskAction = (
  projectId: number,
  tasks: Task[],
  taskIndex: number,
  subtaskIndex: number,
  role: string
): SubTask => {
  const sub_tasks = tasks[taskIndex].sub_tasks;
  const subTaskId = sub_tasks.length > 0 ? Math.max(...sub_tasks.map((i) => i.sub_task_id)) + 1 : 1;
  const newSubTask: SubTask = {
    sub_task_id: subTaskId,
    project_id: projectId,
    comments_count: 0,
    task_id: tasks[taskIndex].task_id!,
    task: "",
    status: "Not Started",
    hours_tracked: 0,
    notInserted: true,
    archived: false,
    position: 0,
    hours_allotted: 0
  };

  insertProjectTask(
    projectId, newSubTask.task_id, newSubTask.sub_task_id,
    newSubTask,
    role
  );

  if (subtaskIndex >= 0) {
    sub_tasks.splice(subtaskIndex + 1, 0, newSubTask);
  } else {
    sub_tasks.push(newSubTask);
  }

  tasks[taskIndex].sub_tasks = sub_tasks;            // replace with the clone before modifying
  return newSubTask;
};


export function updateTaskCheckedState(task: Task, isChecked: boolean): Task {
  return {
    ...task,
    checked: isChecked,
    sub_tasks: task.sub_tasks.map((subTask) => ({
      ...subTask,
      checked: isChecked,
    })),
  };
}
